import type { Building } from "building-types/building/buildingTypes";
import type { ComponentTransformVerticalPlacement } from "building-types/components/componentTypes";
import type {
  DimensionEdgeBehavior,
  EdgeBehavior,
  ElevationNoteEdgeBehavior,
  FoundationAssemblyEdgeBehavior,
  FoundationPatternEdgeBehavior,
  LabelEdgeBehavior,
  PorchColumnEdgeBehavior,
  TrimEdgeBehavior,
  WallAssemblyEdgeBehavior,
} from "building-types/edgeBehavior/edgeBehavior";
import { EdgeBehaviorType } from "building-types/edgeBehavior/edgeBehaviorTypesEnum";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type { Level } from "building-types/levels/levelTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RelatedRoomsMap } from "building-types/rooms/relatedRoomsMapTypes";
import type { RelatedStairSystemsMap } from "building-types/stairs/relatedStairSystemsMapTypes";
import type { StairBlock } from "building-types/stairs/stairTypes";
import type { RWTable, Table } from "common/data/table";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { componentInstanceLevelId } from "../generationTools/components/componentInstanceLevel";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { edgeBehaviors, optionSets, instanceOptionSets, stairBlocks },
  optionChoices,
  relatedRoomsMap,
  relatedStairSystemsMap,
  levels,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    edgeBehaviors,
    stairBlocks,
    relatedRoomsMap,
    relatedStairSystemsMap: relatedStairSystemsMap ?? {},
    levels: levels ?? {},
    definitions,
    optionChoices: resolveOptionsInFormula(edgeBehaviors, definitions, optionChoices),
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  definitions: "shallow",
  relatedRoomsMap: "shallow",
  relatedStairSystemsMap: "shallow",
  stairBlocks: "shallow",
  levels: "shallow",
});

/**
 * Split edge behaviors into typed tables for later generation steps.
 *
 * Room- and stair-block-parented behaviors are expanded per component instance: formulas are
 * evaluated with that instance's option choices, disabled behaviors are dropped, and `parentId`
 * is remapped to the instance host.
 */
function generate({
  edgeBehaviors,
  stairBlocks,
  relatedRoomsMap,
  relatedStairSystemsMap,
  levels,
  definitions,
  optionChoices,
}: ReturnType<typeof select>): Pick<
  Building,
  | "elevationNoteEdgeBehaviors"
  | "wallAssemblyEdgeBehaviors"
  | "dimensionEdgeBehaviors"
  | "porchColumnEdgeBehaviors"
  | "trimEdgeBehaviors"
  | "foundationAssemblyEdgeBehaviors"
  | "foundationPatternEdgeBehaviors"
  | "labelEdgeBehaviors"
> {
  const elevationNoteEdgeBehaviors: RWTable<ElevationNoteEdgeBehavior> = {};
  const wallAssemblyEdgeBehaviors: RWTable<WallAssemblyEdgeBehavior> = {};
  const dimensionEdgeBehaviors: RWTable<DimensionEdgeBehavior> = {};
  const porchColumnEdgeBehaviors: RWTable<PorchColumnEdgeBehavior> = {};
  const trimEdgeBehaviors: RWTable<TrimEdgeBehavior> = {};
  const foundationAssemblyEdgeBehaviors: RWTable<FoundationAssemblyEdgeBehavior> = {};
  const foundationPatternEdgeBehaviors: RWTable<FoundationPatternEdgeBehavior> = {};
  const labelEdgeBehaviors: RWTable<LabelEdgeBehavior> = {};

  for (const archetypeId in edgeBehaviors) {
    const edgeBehaviorArchetype = edgeBehaviors[archetypeId];
    if (!edgeBehaviorArchetype) {
      continue;
    }

    for (const edgeBehavior of expandAndEvaluateEdgeBehavior(
      edgeBehaviorArchetype,
      relatedRoomsMap,
      relatedStairSystemsMap,
      definitions,
      optionChoices,
      stairBlocks,
      levels,
    )) {
      const id = edgeBehavior.id;
      switch (edgeBehavior.edgeBehaviorType) {
        case EdgeBehaviorType.ELEVATION_NOTE:
          elevationNoteEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.WALL_ASSEMBLY:
          wallAssemblyEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.DIMENSION:
          dimensionEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.PORCH_COLUMN:
          porchColumnEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.TRIM:
          trimEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.FOUNDATION_ASSEMBLY:
          foundationAssemblyEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.FOUNDATION_PATTERN:
          foundationPatternEdgeBehaviors[id] = edgeBehavior;
          break;
        case EdgeBehaviorType.LABEL:
          labelEdgeBehaviors[id] = edgeBehavior;
          break;
      }
    }
  }

  return {
    elevationNoteEdgeBehaviors,
    wallAssemblyEdgeBehaviors,
    dimensionEdgeBehaviors,
    porchColumnEdgeBehaviors,
    trimEdgeBehaviors,
    foundationAssemblyEdgeBehaviors,
    foundationPatternEdgeBehaviors,
    labelEdgeBehaviors,
  };
}

interface RelatedHostTarget {
  hostId: string;
  componentInstanceChain: readonly string[];
  verticalPlacement?: ComponentTransformVerticalPlacement;
}

function expandAndEvaluateEdgeBehavior(
  edgeBehavior: EdgeBehavior,
  relatedRoomsMap: RelatedRoomsMap,
  relatedStairSystemsMap: RelatedStairSystemsMap,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  stairBlocks: Table<StairBlock>,
  levels: Table<Level>,
): EdgeBehavior[] {
  const relatedRooms = relatedRoomsMap[edgeBehavior.parentId] ?? [];
  const instanceRoomTargets = relatedRooms
    .map((related) => ({
      hostId: related.roomId,
      componentInstanceChain: related.componentInstanceChain ?? [],
    }))
    .filter((target) => target.componentInstanceChain.length > 0);

  if (instanceRoomTargets.length > 0) {
    return evaluateAndRemapEdgeBehaviorForInstanceTargets(
      edgeBehavior,
      instanceRoomTargets,
      definitions,
      optionChoices,
      levels,
    );
  }
  const stairBlock = stairBlocks[edgeBehavior.parentId];
  if (stairBlock) {
    const relatedStairSystems = relatedStairSystemsMap[stairBlock.stairSystemId] ?? [];
    const instanceStairSystemTargets = relatedStairSystems
      .map((related) => ({
        hostId: instanceIdForComponent(stairBlock.id, related.componentInstanceChain ?? []),
        componentInstanceChain: related.componentInstanceChain ?? [],
        verticalPlacement: related.verticalPlacement,
      }))
      .filter((target) => target.componentInstanceChain.length > 0);
    if (instanceStairSystemTargets.length > 0) {
      return evaluateAndRemapEdgeBehaviorForInstanceTargets(
        edgeBehavior,
        instanceStairSystemTargets,
        definitions,
        optionChoices,
        levels,
      );
    }
  }
  const evaluated = evaluateEntityForComponentInstance(edgeBehavior, [], definitions, optionChoices);
  return evaluated.disabled ? [] : [evaluated];
}

function evaluateAndRemapEdgeBehaviorForInstanceTargets(
  edgeBehavior: EdgeBehavior,
  instanceTargets: readonly RelatedHostTarget[],
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  levels: Table<Level>,
) {
  const results: EdgeBehavior[] = [];
  for (const relatedEntity of instanceTargets) {
    const componentInstanceChain = relatedEntity.componentInstanceChain ?? [];
    const evaluated = evaluateEntityForComponentInstance(
      edgeBehavior,
      componentInstanceChain,
      definitions,
      optionChoices,
    );
    if (evaluated.disabled) {
      continue;
    }
    const remapped = remapEdgeBehaviorOntoInstance(
      evaluated,
      componentInstanceChain,
      relatedEntity.hostId,
      relatedEntity.verticalPlacement,
      levels,
    );
    results.push(remapped);
  }
  return results;
}

function remapEdgeBehaviorOntoInstance(
  edgeBehavior: EdgeBehavior,
  componentInstanceChain: readonly string[],
  instanceHostId: string,
  verticalPlacement: ComponentTransformVerticalPlacement | undefined,
  levels: Table<Level>,
): EdgeBehavior {
  const remapped: EdgeBehavior = {
    ...edgeBehavior,
    id: instanceIdForComponent(edgeBehavior.id, componentInstanceChain),
    parentId: instanceHostId,
  };

  if (
    remapped.edgeBehaviorType !== EdgeBehaviorType.FOUNDATION_PATTERN ||
    !verticalPlacement ||
    remapped.foundationPattern.patternType !== FoundationSectionSizeType.ISOLATED ||
    !remapped.foundationPattern.foundationLevelId
  ) {
    return remapped;
  }

  return {
    ...remapped,
    foundationPattern: {
      ...remapped.foundationPattern,
      foundationLevelId: componentInstanceLevelId(
        remapped.foundationPattern.foundationLevelId,
        levels,
        verticalPlacement,
        [...componentInstanceChain],
      ),
    },
  };
}

export default memo(select, generate, compare);
